import { logger } from "@platform/browser-logger";
import {
  extractErrorMessage,
  useHeyApi,
  rfpsSetCoverageCompletion,
} from "@platform/api";
import { COVERAGE_ORDER } from "@platform/types";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import axios, { AxiosInstance } from "axios";
import { useState } from "react";

import { CoverageOverviewResponse, CoverageStatus } from "../types";

import type { CoverageTypeEnum, BenefitPlanTypeEnum } from "@platform/api";

const COVERAGE_SUMMARY_ENDPOINT = "coverage-summary";
const PLAN_BUILDER_DATA_QUERY_KEY = "planBuilderData";

type CoverageItem = CoverageOverviewResponse["existingCoverages"][number];

/**
 * Sort coverages by COVERAGE_ORDER using frontend name mapping
 */
const sortCoveragesByOrderAndRemoveDuplicates = (
  coverages: CoverageItem[],
  backendToFrontendMap: Record<string, string>,
): CoverageItem[] =>
  [...coverages]
    .sort((a, b) => {
      const mappedA = backendToFrontendMap[a.name] || a.name;
      const mappedB = backendToFrontendMap[b.name] || b.name;

      const indexA = COVERAGE_ORDER.indexOf(mappedA);
      const indexB = COVERAGE_ORDER.indexOf(mappedB);

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .filter((coverage, index, self) =>
      index === 0 ? true : coverage.name !== self[index - 1].name,
    );

/**
 * Deduplicate and rebuild coverage arrays, preferring new coverages over existing
 */
const deduplicateCoverages = (
  sortedExisting: CoverageItem[],
  sortedNew: CoverageItem[],
): { finalExisting: CoverageItem[]; finalNew: CoverageItem[] } => {
  const coverageMap = new Map<string, CoverageItem>();
  const newCoverageNames = new Set(sortedNew.map((c) => c.name));

  // Add existing coverages to map
  sortedExisting.forEach((coverage) =>
    coverageMap.set(coverage.name, coverage),
  );

  // Add new coverages to map (overwrites duplicates)
  sortedNew.forEach((coverage) => {
    if (coverageMap.has(coverage.name)) {
      logger.error(
        `[Coverage Overview] Duplicate coverage found across existing and new: "${coverage.name}". Using new coverage.`,
      );
    }
    coverageMap.set(coverage.name, coverage);
  });

  // Filter existing coverages that aren't in new, then get deduplicated version from map
  const finalExisting = sortedExisting
    .filter((coverage) => !newCoverageNames.has(coverage.name))
    .map((coverage) => coverageMap.get(coverage.name)!);

  // Get deduplicated new coverages from map
  const finalNew = sortedNew.map((coverage) => coverageMap.get(coverage.name)!);

  return { finalExisting, finalNew };
};

export interface UseCoverageOverviewResult {
  coverageQuery: UseQueryResult<CoverageOverviewResponse>;
  ecoRfpId: string | undefined;

  // Legacy
  setCompletionStatusLegacy: (
    params: SetCompletionStatusLegacyParams,
  ) => Promise<void>;
  isSettingCompletionLegacy: boolean;
  invalidateCompletionStatus: () => void;
  getCompletionStatusLegacy: (coverageName: string) => boolean | undefined;

  // New (non-legacy)
  getCompletionStatus: (coverageName: string) => boolean | undefined;
  setCompletionStatus: (params: SetCompletionStatusParams) => Promise<void>;
  isSettingCompletion: boolean;
}

/**
 * Parameters for setting completion status legacy
 */
export interface SetCompletionStatusLegacyParams {
  /** Coverage type (V5 enum value like "dental", "vision") */
  coverageType: CoverageTypeEnum;
  /** Optional product type (e.g., "ppo", "hmo") */
  productType?: BenefitPlanTypeEnum;
  /** Whether the coverage is complete */
  isComplete: boolean;
}

interface UseCoverageOverviewParams {
  ecoRfpId: string; // V5 ECO RFP ID (required)
  axiosInstance?: AxiosInstance;
}

/**
 * Parameters for setting completion status
 */
export interface SetCompletionStatusParams {
  /** Coverage type (V5 enum value like "dental", "vision") */
  coverageType: CoverageTypeEnum;
  /** Optional product type (e.g., "ppo", "hmo") */
  productType?: BenefitPlanTypeEnum;
  /** Completion status string for /completion-status endpoint */
  status: "complete" | "incomplete" | "awaiting-input";
}

// ============================================================
// STATUS MAPPING HELPERS
// ============================================================

export const useCoverageOverview = ({
  ecoRfpId,
  axiosInstance = axios,
}: UseCoverageOverviewParams): UseCoverageOverviewResult => {
  const queryClient = useQueryClient();
  const { btxClient } = useHeyApi();
  const [isAwaitingStatusUpdate, setIsAwaitingStatusUpdate] = useState(false);

  const coverageQueryKey = [COVERAGE_SUMMARY_ENDPOINT, ecoRfpId];
  const planBuilderQueryKey = [PLAN_BUILDER_DATA_QUERY_KEY, ecoRfpId];

  // Map backend coverage names to COVERAGE_ORDER names for sorting
  const backendToFrontendMap: Record<string, string> = {
    STD: "Short Term Disability",
    LTD: "Long Term Disability",
    "Basic Life & AD&D": "Basic Life/AD&D",
    "Voluntary Life & AD&D": "Voluntary Life/AD&D",
    Accident: "Accident Insurance",
    "Critical Illness": "Critical Illness Insurance",
    "Hospital Indemnity": "Hospital Indemnity Insurance",
    Cancer: "Cancer Insurance",
    EAP: "Employee Assistance Program",
    FMLA: "Family Medical Leave Administration",
    // These match already, but included for completeness
    Dental: "Dental",
    "Dental PPO": "Dental PPO",
    "Dental HMO": "Dental HMO",
    Vision: "Vision",
    "Vision PPO": "Vision PPO",
    "Whole Life": "Whole Life",
    "Individual Disability Insurance": "Individual Disability Insurance",
  };

  const coverageQuery = useQuery({
    queryKey: coverageQueryKey,
    queryFn: async ({ queryKey }) => {
      const { data } = await axiosInstance.get<CoverageOverviewResponse>(
        `btx/v1/rfps/${ecoRfpId}/${COVERAGE_SUMMARY_ENDPOINT}`,
      );

      // Guard: If both existingCoverages and newCoverages are undefined,
      // this is a 304 response where axios didn't return the cached body.
      // Return the previous cached data from React Query.
      if (
        data?.existingCoverages === undefined &&
        data?.newCoverages === undefined
      ) {
        const previousData =
          queryClient.getQueryData<CoverageOverviewResponse>(queryKey);
        if (previousData) {
          logger.info(
            "[Coverage Overview] Received 304 with undefined data, using previously cached data",
            { ecoRfpId },
          );
          return previousData;
        }
        // If there's no previous data, return empty structure
        return {
          existingCoverages: [],
          newCoverages: [],
          allCoverages: [],
          coveragesStatus: "incomplete" as CoverageStatus,
        };
      }

      // Defensive: ensure arrays are valid before processing
      const existingCoverages = Array.isArray(data?.existingCoverages)
        ? data.existingCoverages
        : [];
      const newCoverages = Array.isArray(data?.newCoverages)
        ? data.newCoverages
        : [];

      if (!Array.isArray(data?.existingCoverages)) {
        logger.warn(
          "[Coverage Overview] existingCoverages is not an array, defaulting to empty",
          { received: typeof data?.existingCoverages, ecoRfpId },
        );
      }
      if (!Array.isArray(data?.newCoverages)) {
        logger.warn(
          "[Coverage Overview] newCoverages is not an array, defaulting to empty",
          { received: typeof data?.newCoverages, ecoRfpId },
        );
      }

      // Sort coverages by COVERAGE_ORDER using the mapping
      const sortedExisting = sortCoveragesByOrderAndRemoveDuplicates(
        existingCoverages,
        backendToFrontendMap,
      );
      const sortedNew = sortCoveragesByOrderAndRemoveDuplicates(
        newCoverages,
        backendToFrontendMap,
      );

      // Deduplicate across both arrays, preferring new coverages
      const { finalExisting, finalNew } = deduplicateCoverages(
        sortedExisting,
        sortedNew,
      );

      return {
        ...data,
        existingCoverages: finalExisting,
        newCoverages: finalNew,
        allCoverages: sortCoveragesByOrderAndRemoveDuplicates(
          [...finalExisting, ...finalNew],
          backendToFrontendMap,
        ),
      };
    },
    enabled: Boolean(ecoRfpId),
  });

  // ============================================================
  // LEGACY COMPLETION STATUS
  // ============================================================

  const setCompletionMutationLegacy = useMutation({
    mutationFn: async (params: SetCompletionStatusLegacyParams) => {
      if (!ecoRfpId) {
        const error = new Error("No ecoRfpId available");
        logger.error(
          "[Coverage Overview] Cannot set completion status - no ecoRfpId",
          error,
          { params },
        );
        throw error;
      }

      const path = {
        id: ecoRfpId,
        coverageType: params.coverageType,
      };

      const query: { productType?: BenefitPlanTypeEnum } | undefined =
        params.productType ? { productType: params.productType } : undefined;

      await rfpsSetCoverageCompletion({
        client: btxClient,
        body: { isComplete: params.isComplete },
        path,
        query,
        throwOnError: true,
      });
    },
    onSuccess: async (_, variables) => {
      setIsAwaitingStatusUpdate(true);
      try {
        await queryClient.refetchQueries(
          {
            queryKey: [COVERAGE_SUMMARY_ENDPOINT],
          },
          {
            throwOnError: true,
          },
        );

        logger.info(
          "[Coverage Overview] Successfully updated completion status",
          { variables },
        );
      } catch (error) {
        logger.error(
          "[Coverage Overview] Failed to refetch coverage summary after completion update",
          error as Error,
          { variables },
        );
        throw error;
      } finally {
        setIsAwaitingStatusUpdate(false);
      }
    },
    onError: (error: unknown, variables) => {
      setIsAwaitingStatusUpdate(false);

      const errorMessage = extractErrorMessage(
        error,
        "Failed to update completion status",
      );

      logger.error(
        "[Coverage Overview] Failed to update completion status",
        error as Error,
        {
          variables,
          errorMessage,
        },
      );
    },
  });

  // Function to invalidate completion status (triggers refetch)
  // Invalidates all coverage-summary queries across the application
  const invalidateCompletionStatus = (): void => {
    queryClient.invalidateQueries({
      queryKey: [COVERAGE_SUMMARY_ENDPOINT],
    });
  };

  const getCompletionStatusLegacy = (
    coverageName: string,
  ): boolean | undefined => {
    const data = coverageQuery.data;
    if (!data) return undefined;
    const allCoverages = data.allCoverages ?? [];
    const coverage = allCoverages.find((c) => c.name === coverageName);

    return coverage?.isComplete;
  };

  // Wrapper to return Promise for easier async/await usage
  const setCompletionStatusLegacy = async (
    params: SetCompletionStatusLegacyParams,
  ): Promise<void> => {
    await setCompletionMutationLegacy.mutateAsync(params);
  };

  // ============================================================
  // NEW COMPLETION STATUS FUNCTIONS (non-legacy)
  // ============================================================

  /**
   * Mutation for setting coverage completion status via completion-status endpoint.
   */
  const setCompletionMutation = useMutation({
    mutationFn: async (params: SetCompletionStatusParams) => {
      if (!ecoRfpId) {
        const error = new Error("No ecoRfpId available");
        logger.error(
          "[Coverage Overview] Cannot set completion status - no ecoRfpId",
          error,
          { params },
        );
        throw error;
      }

      const query: { productType?: BenefitPlanTypeEnum } | undefined =
        params.productType ? { productType: params.productType } : undefined;

      await axiosInstance.post(
        `btx/v1/rfps/${ecoRfpId}/coverages/${params.coverageType}/completion-status`,
        { status: params.status },
        { params: query },
      );
    },
    onSuccess: async (_, variables) => {
      setIsAwaitingStatusUpdate(true);
      try {
        await queryClient.refetchQueries(
          { queryKey: [COVERAGE_SUMMARY_ENDPOINT] },
          { throwOnError: true },
        );

        if (ecoRfpId) {
          await queryClient.refetchQueries(
            {
              queryKey: planBuilderQueryKey,
            },
            {
              throwOnError: true,
            },
          );
        }

        logger.info(
          "[Coverage Overview] Successfully updated completion status",
          { variables },
        );
      } catch (error) {
        logger.error(
          "[Coverage Overview] Failed to refetch coverage summary after completion update",
          error as Error,
          { variables },
        );
        throw error;
      } finally {
        setIsAwaitingStatusUpdate(false);
      }
    },
    onError: (error: unknown, variables) => {
      setIsAwaitingStatusUpdate(false);
      const errorMessage = extractErrorMessage(
        error,
        "Failed to update completion status",
      );
      logger.error(
        "[Coverage Overview] Failed to update completion status",
        error as Error,
        { variables, errorMessage },
      );
    },
  });

  const getCompletionStatus = (coverageName: string): boolean | undefined => {
    const data = coverageQuery.data;
    if (!data) return undefined;
    const allCoverages = data.allCoverages ?? [];
    const coverage = allCoverages.find((c) => c.name === coverageName);

    if (coverage?.status) {
      return coverage.status === "complete";
    }

    if (coverage?.isComplete === undefined) return undefined;

    return coverage.isComplete;
  };

  const setCompletionStatus = async (
    params: SetCompletionStatusParams,
  ): Promise<void> => {
    await setCompletionMutation.mutateAsync(params);
  };

  return {
    coverageQuery,
    ecoRfpId,

    // Legacy
    setCompletionStatusLegacy,
    isSettingCompletionLegacy:
      setCompletionMutationLegacy.isPending || isAwaitingStatusUpdate,
    invalidateCompletionStatus,
    getCompletionStatusLegacy,

    // New (non-legacy)
    setCompletionStatus,
    isSettingCompletion:
      setCompletionMutation.isPending || isAwaitingStatusUpdate,
    getCompletionStatus,
  };
};
